import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { api } from '../App';
import Button from '@shared/ui/Button';
import {
  confirmPasswordField,
  newPasswordField,
  newPasswordsMatch,
  PASSWORD_HINT,
} from '../components/auth/credentialSchema';

const resetPasswordSchema = z
  .object({
    newPassword: newPasswordField,
    confirmPassword: confirmPasswordField,
  })
  .refine(newPasswordsMatch.check, newPasswordsMatch.options);

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery(
    ['validate-reset-token', token],
    async () => {
      const response = await api.get(`/auth/validate-reset-token/${token}`);
      return response.data;
    },
    {
      enabled: !!token,
      retry: false,
    },
  );

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const resetMutation = useMutation(
    async (values: ResetPasswordFormData) => {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword: values.newPassword,
      });
      return response.data;
    },
  );

  const onSubmit = (values: ResetPasswordFormData) => {
    resetMutation.mutate(values, {
      onSuccess: () => {
        // Optionally redirect after success
        // navigate('/login');
      },
    });
  };

  const isSubmitting = resetMutation.isLoading || form.formState.isSubmitting;

  const isInvalid = isError || data?.success === false;
  const isSuccess = resetMutation.isSuccess;

  return (
    <div className="min-h-screen bg-white dark:bg-background-dark-secondary flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full card">
        <div className="p-6 space-y-4">
          <div className="flex items-center space-x-2">
            <Lock className="h-5 w-5 text-primary-700 dark:text-primary-400" />
            <h1 className="text-xl font-bold text-text-primary dark:text-text-dark-primary">
              Reset your password
            </h1>
          </div>

          {isLoading && (
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              Validating your reset link…
            </p>
          )}

          {!isLoading && isInvalid && !isSuccess && (
            <div className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700">
              This reset link is invalid or has expired. Please request a new password reset.
            </div>
          )}

          {!isLoading && !isInvalid && !isSuccess && (
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              Choose a new password for your account.
            </p>
          )}

          {isSuccess && (
            <div className="rounded-md border border-border-light dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary px-3 py-2 text-sm text-text-secondary dark:text-text-dark-secondary">
              Your password has been reset. You can now{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-primary-700 dark:text-primary-400 hover:underline"
              >
                sign in
              </button>
              .
            </div>
          )}

          {!isLoading && !isInvalid && !isSuccess && (
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1"
                >
                  New password
                </label>
                <input
                  id="newPassword"
                  type="password"
                  className="input"
                  {...form.register('newPassword')}
                  aria-invalid={!!form.formState.errors.newPassword}
                />
                {form.formState.errors.newPassword ? (
                  <p className="mt-1 text-xs text-error-600">
                    {form.formState.errors.newPassword.message}
                  </p>
                ) : (
                  /* Stated up front — this page used to reveal the minimum only
                     by rejecting a password the user had already chosen. */
                  <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    {PASSWORD_HINT}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1"
                >
                  Confirm new password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  className="input"
                  {...form.register('confirmPassword')}
                  aria-invalid={!!form.formState.errors.confirmPassword}
                />
                {form.formState.errors.confirmPassword && (
                  <p className="mt-1 text-xs text-error-600">
                    {form.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                variant="primary"
              className="w-full disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Resetting password…' : 'Reset password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

